import React, { useEffect, useState, useRef } from 'react';
import { Modal, Button, FormGroup, InputGroup, InputGroupAddon, InputGroupText, Input } from 'reactstrap';
import { ClipLoader } from 'react-spinners';
import { connect } from 'react-redux';
import api from '../../../../Api';

export function AddUserCompanyModal({ isOpen, openModal, toggle, companyId, onChanged, notify, onBack, translate = {} }){
  const [loading, setLoading] = useState(false);
  const [searchUser, setSearchUser] = useState([]);
  const [term, setTerm] = useState('');
  const [mode, setMode] = useState('view');
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!(openModal || isOpen)) return;
    if (mode === 'view') {
      loadAll();
    } else {
      setSearchUser([]);
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [openModal, isOpen, companyId, mode]);

  const loadAll = async () => {
    if (!companyId) return setSearchUser([]);
    setLoading(true);
    try {
      const res = await api.searchManagersCompanyAll(companyId);
      const data = Array.isArray(res?.data) ? res.data : [];
      setSearchUser(data);
    } catch (err) {
      console.error('Erro ao buscar gestores:', err);
      setSearchUser([]);
    } finally {
      setLoading(false);
    }
  };

  const doSearch = (t) => {
    if (mode !== 'add') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(t);
    }, 400);
  };

  const performSearch = async (t) => {
    if (!companyId) return;
    setLoading(true);
    try {
      if (!t || t.trim() === '') {
        await loadAll();
        return;
      }
      const res = await api.searchManagersCompanyTerm(companyId, t);
      const data = Array.isArray(res?.data) ? res.data : [];
      setSearchUser(data);
    } catch (err) {
      console.error('Erro na busca de gestores:', err);
      setSearchUser([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (userId) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.addUserCompany(companyId, userId);
      if (res && res.data) {
        if (typeof notify === 'function') {
          notify({ place: 'tr', message: (<div className="alert-text"><span className="alert-title">{translate['sucesso'] || 'Sucesso'}.</span><span data-notify="message">{translate['o_usuario_foi_adicinado_a_unidade'] || 'O usuário foi adicionado à unidade'}</span></div>), type: 'success', icon: 'ni ni-check-bold', autoDismiss: 7 });
        }
        if (typeof onChanged === 'function') onChanged();
        await performSearch(term);
      }
    } catch (err) {
      console.error('Erro ao adicionar usuário:', err);
      if (err?.response?.data?.msg) {
        if (typeof notify === 'function') {
          notify({ place: 'tr', message: (<div className="alert-text"><span className="alert-title">{translate['ops'] || 'Ops'}.</span><span data-notify="message">{err.response.data.msg}</span></div>), type: 'danger', icon: 'ni ni-check-bold', autoDismiss: 7 });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.deleteUserCompany(companyId, userId);
      if (res && res.data) {
        if (typeof notify === 'function') {
          notify({ place: 'tr', message: (<div className="alert-text"><span className="alert-title">{translate['sucesso'] || 'Sucesso'}.</span><span data-notify="message">{translate['o_usuario_foi_removido_da_unidade'] || 'O usuário foi removido da unidade'}</span></div>), type: 'success', icon: 'ni ni-check-bold', autoDismiss: 7 });
        }
        if (typeof onChanged === 'function') onChanged();
        await performSearch(term);
      }
    } catch (err) {
      console.error('Erro ao remover usuário:', err);
      if (err?.response?.data?.msg) {
        if (typeof notify === 'function') {
          notify({ place: 'tr', message: (<div className="alert-text"><span className="alert-title">{translate['ops'] || 'Ops'}.</span><span data-notify="message">{err.response.data.msg}</span></div>), type: 'danger', icon: 'ni ni-check-bold', autoDismiss: 7 });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="modal-dialog-centered" size="lg" isOpen={isOpen}>
      <div className="modal-header bg-secondary d-flex align-items-center justify-content-between">
        <div>
          <h5 className="modal-title mb-0">{translate['adicionar_ou_remover_gestores'] || 'Adicionar ou remover gestores'}</h5>
          <small className="text-muted">{mode === 'view' ? (translate['listagem_gestores'] || 'Lista de gestores da unidade') : (translate['buscar_e_adicionar'] || 'Pesquisar e adicionar gestores')}</small>
        </div>
        <div className="d-flex align-items-center">
          <div className="btn-group mr-3" role="group" aria-label="mode-toggle">
            <button
              type="button"
              className={`btn ${mode === 'view' ? 'btn-primary' : 'btn-outline-primary'} btn-md px-3`}
              onClick={() => setMode('view')}
            >
              <i className="fas fa-users mr-2" />
              {translate['ver_gestores'] || 'Ver gestores'}
            </button>
            <button
              type="button"
              className={`btn ${mode === 'add' ? 'btn-primary' : 'btn-outline-primary'} btn-md px-3`}
              onClick={() => setMode('add')}
            >
              <i className="fas fa-user-plus mr-2" />
              {translate['adicionar_gestores'] || 'Adicionar gestores'}
            </button>
          </div>
          <button aria-label="Close" className="close text-white" data-dismiss="modal" type="button" onClick={toggle}>
            <span aria-hidden={true}>×</span>
          </button>
        </div>
      </div>
      <div className="modal-body bg-secondary">
        {mode === 'add' && (
          <FormGroup>
            <h5 className="mb-2">{translate['pesquisar'] || 'Pesquisar'}</h5>
            <InputGroup className="input-group-alternative mb-4">
              <InputGroupAddon addonType="prepend"><InputGroupText><i className="ni ni-zoom-split-in" /></InputGroupText></InputGroupAddon>
              <Input className="form-control-alternative" placeholder={translate['procurar'] || 'Procurar'} type="text" onChange={(e) => { setTerm(e.target.value); doSearch(e.target.value); }} />
            </InputGroup>
          </FormGroup>
        )}

        <div className="list-group mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div className="text-center py-5 w-100">
              <ClipLoader sizeUnit={"px"} size={50} color={'#172b4d'} loading={loading} />
            </div>
          ) : (searchUser && searchUser.length > 0) ? (
            searchUser.map(u => (
              <div key={`aaq_${u.id}`} className="list-group-item list-group-item-action d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div className="avatar-circle mr-3">
                    <img alt="..." src={u.photo} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: '50%' }} />
                  </div>
                  <div>
                    <div className="text-dark font-weight-600 text-sm">{u.name} {u.lastName}</div>
                    <small className="d-block text-muted">{u.user}</small>
                  </div>
                </div>
                <div>
                  {(u.incompany === undefined) ? (
                    mode === 'add' ? (
                      <button onClick={() => handleAdd(u.id)} type="button" className="btn-icon btn btn-primary btn-sm"><span className="btn-inner--icon"><i className="ni ni-fat-add"></i></span><span className="btn-inner--text">{translate['adicionar'] || 'Adicionar'}</span></button>
                    ) : null
                  ) : (
                    <button onClick={() => handleRemove(u.id)} type="button" className="btn-icon btn btn-danger btn-sm"><span className="btn-inner--icon"><i className="ni ni-fat-remove"></i></span><span className="btn-inner--text">{translate['remover'] || 'Remover'}</span></button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-4 w-100">
              {mode === 'add' ? (translate['nenhum_usuario_foi_encontrado'] || 'Nenhum usuário foi encontrado') : (translate['nenhum_gestor_encontrado'] || 'Nenhum gestor encontrado')}
            </div>
          )}
        </div>
      </div>
      <div className="modal-footer bg-secondary d-flex justify-content-between w-100">
        <div>{onBack && (<Button color="link" onClick={onBack}><i className="fas fa-arrow-left mr-1"></i> {translate['voltar'] || 'Voltar'}</Button>)}</div>
        <div><Button color="default" onClick={toggle}>{translate['fechar'] || 'Fechar'}</Button></div>
      </div>
    </Modal>
  );
}

export default connect(state => ({ translate: state.translate }))(AddUserCompanyModal);
